import type { ReactNode } from 'react'
import { GripVertical, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type ConfigBlockChromeProps = {
  /** Kind / type badge label shown in the header. */
  kindLabel: string
  /** Optional secondary badges (weight %, locked, etc.). */
  badges?: ReactNode
  title?: string
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>
  setNodeRef?: (node: HTMLElement | null) => void
  style?: React.CSSProperties
  isDragging?: boolean
  onRemove?: () => void
  hideDrag?: boolean
  hideRemove?: boolean
  locked?: boolean
  children: ReactNode
  footer?: ReactNode
  className?: string
  errorText?: string
}

/**
 * Typed authoring block shell: kind badge, optional drag handle, remove, body slot.
 * Used by classifier config blocks and rubric criterion cards.
 */
export function ConfigBlockChrome({
  kindLabel,
  badges,
  title,
  dragHandleProps,
  setNodeRef,
  style,
  isDragging = false,
  onRemove,
  hideDrag = false,
  hideRemove = false,
  locked = false,
  children,
  footer,
  className,
  errorText,
}: ConfigBlockChromeProps) {
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'space-y-3 rounded-md border bg-background p-4',
        isDragging && 'opacity-80 shadow-md',
        locked && 'border-dashed bg-muted/20',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {!hideDrag && dragHandleProps ? (
            <button
              type="button"
              className="shrink-0 cursor-grab text-muted-foreground hover:text-foreground"
              aria-label="Drag to reorder"
              {...dragHandleProps}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          ) : null}
          <Badge variant="outline">{kindLabel}</Badge>
          {locked ? <Badge variant="secondary">Locked</Badge> : null}
          {badges}
          {title ? <span className="truncate text-sm font-medium">{title}</span> : null}
        </div>
        {!hideRemove && onRemove ? (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4" aria-hidden />
            Remove
          </Button>
        ) : null}
      </div>
      {children}
      {footer}
      {errorText ? <p className="text-xs text-destructive">{errorText}</p> : null}
    </div>
  )
}
